import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Eye, RefreshCw } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Modal } from '../../components/Modal';
import { PageHeader } from '../../components/PageHeader';
import { Skeleton } from '../../components/Skeleton';
import { useWebhookDeliveries } from '../../api/admin';
import { formatTime } from '../../lib/format';
import type { WebhookDelivery } from '../../mocks/admin';

/** Webhook deliveries — table with payload viewer modal. */
export function Webhooks() {
  const { data: webhookDeliveries = [], isLoading, isFetching } = useWebhookDeliveries();
  const queryClient = useQueryClient();
  const [viewing, setViewing] = useState<WebhookDelivery | null>(null);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Platform"
        title="Webhook deliveries"
        subtitle="Outbound events to external systems (Laravel POS, accounting, etc.)."
        action={
          <Button
            variant="secondary"
            size="sm"
            iconLeft={<RefreshCw className="w-4 h-4" />}
            loading={isFetching}
            onClick={() => void queryClient.invalidateQueries({ queryKey: ['admin', 'webhooks'] })}
          >
            Refresh
          </Button>
        }
      />

      <Card className="overflow-hidden">
        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
              <tr>
                <th className="text-left font-medium px-5 sm:px-6 py-3">When</th>
                <th className="text-left font-medium px-4 py-3">Event</th>
                <th className="text-left font-medium px-4 py-3">Target</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-right font-medium px-4 py-3 hidden md:table-cell">Attempt</th>
                <th className="px-5 sm:px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    <td className="px-5 sm:px-6 py-3"><Skeleton className="h-4 w-24 rounded" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-40 max-w-full rounded" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-28 rounded" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-5 w-16 rounded-full" /></td>
                    <td className="px-4 py-3 hidden md:table-cell"><Skeleton className="h-4 w-8 rounded ml-auto" /></td>
                    <td className="px-5 sm:px-6 py-3"></td>
                  </tr>
                ))
              ) : webhookDeliveries.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    No webhook deliveries found.
                  </td>
                </tr>
              ) : (
                webhookDeliveries.map((w) => (
                <tr key={w.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                  <td className="px-5 sm:px-6 py-3 tabular-nums text-zinc-700 dark:text-zinc-200">{formatTime(w.at)}</td>
                  <td className="px-4 py-3">
                    <span className="mono text-xs text-zinc-700 dark:text-zinc-200">{w.event}</span>
                  </td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300">
                    <span className="mono text-xs">{w.target}</span>
                    {w.isRetry && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-300">retry</span>}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={w.status >= 200 && w.status < 300 ? 'success' : 'error'}>
                      {w.status} {w.status >= 200 && w.status < 300 ? 'OK' : 'Failed'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-zinc-700 dark:text-zinc-200 hidden md:table-cell">{w.attempt}</td>
                  <td className="px-5 sm:px-6 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setViewing(w)}
                        className="w-8 h-8 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                        title="View payload"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <PayloadModal delivery={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}

function PayloadModal({ delivery, onClose }: { delivery: WebhookDelivery | null; onClose: () => void }) {
  const open = delivery !== null;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={delivery ? `Webhook delivery · ${delivery.event}` : ''}
      size="lg"
      footer={<Button variant="ghost" onClick={onClose}>Close</Button>}
    >
      {delivery && (
        <div className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <Field k="Status" v={<Badge tone={delivery.status < 300 ? 'success' : 'error'}>{delivery.status}</Badge>} />
            <Field k="Attempt" v={<span className="tabular-nums">{delivery.attempt}</span>} />
            <Field k="Target" v={<span className="mono text-xs">{delivery.target}</span>} />
            <Field k="At" v={<span className="tabular-nums">{formatTime(delivery.at)}</span>} />
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            Full request/response bodies aren't surfaced here yet — the dispatcher stores delivery metadata only.
          </p>
        </div>
      )}
    </Modal>
  );
}

function Field({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700">
      <span className="text-xs uppercase tracking-wider font-medium text-zinc-500 dark:text-zinc-400">{k}</span>
      <span className="text-zinc-900 dark:text-zinc-100">{v}</span>
    </div>
  );
}
